import { Button, Container, FlexGrid, Header, Image, Modal, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import useImageSrcBuilder from '../../../lib/hooks/useImageSrcBuilder';
import generateCMSMetaTags from '../../../lib/utils/generateCMSMetaTags';
import { AboutUsPagePropType } from '../../prop-types/cms-page';
import GradientBanner from '../GradientBanner';
import Markup from '../Markup';
import Page from '../Page';
import GlobalContext from '../../context/global';
import styles from './AboutUsPage.module.css';

const cx = classnames.bind(styles);

function buildTeamMember(teamMember = {}) {
  const { firstName, lastName, title: fullTitle } = teamMember;
  const commaPosition = fullTitle?.indexOf(',') || 0;

  return {
    ...teamMember,
    fullName: `${firstName} ${lastName}`,
    mainTitle: commaPosition >= 0 ? fullTitle?.substr(0, commaPosition + 1) : fullTitle,
    subTitle: commaPosition >= 0 && fullTitle?.substr(commaPosition + 1),
  };
}

export default function AboutUsPage({ page }) {
  const executives = page?.member?.filter(teamMember => teamMember?.memberExecutiveTeam);
  const directors = page?.member?.filter(teamMember => !teamMember?.memberExecutiveTeam);
  const heroSection = page?.sections?.find(section => section.name === 'hero');
  const { config = {} } = useContext(GlobalContext);

  const [activeMember, setActiveMember] = useState();
  const imageToSrc = useImageSrcBuilder(config.imagesHost);

  const modalOpenHandlers = useMemo(() => executives?.map(teamMember => () => {
    setActiveMember(teamMember);
  }), [executives]);

  const onCloseModal = useCallback(() => {
    setActiveMember(undefined);
  }, []);

  const modalContent = useMemo(() => {
    const { fullName, bioImage, mainTitle, subTitle, bio } = buildTeamMember(activeMember);
    return (
      <div className={cx('modalContainer')}>
        <div className={cx('modalHeader')}>
          <div className={cx('modalImageContainer')}>
            <Image
              alt={fullName}
              className={cx('modalImage')}
              src={imageToSrc(bioImage)}
            />
          </div>
          <div className={cx('modalInfoContainer', { noSubtitle: !subTitle })}>
            <Text bold color="blue" size="lg">{fullName}</Text>
            <br />
            <Text as="p" bold className={cx('memberTitle')}>
              {mainTitle}
            </Text>
            {!!subTitle && (
              <Text as="p" bold className={cx('memberTitle')}>
                {subTitle}
              </Text>
            )}
          </div>
        </div>
        <div className={cx('bioContainer')}>
          <Text as="p">
            <Markup source={bio} />
          </Text>
        </div>
      </div>
    );
  }, [activeMember, imageToSrc]);

  return (
    <Page compact name="About">
      <Helmet>
        {page?.title && (
          <title>{page.title}</title>
        )}
        {!!page?.meta?.length && generateCMSMetaTags(page.meta, imageToSrc)}
      </Helmet>
      <GradientBanner className={cx('hero')}>
        <Container>
          <Header as="h1" color="white" elongated uppercase>
            <Markup source={heroSection?.title} />
          </Header>
        </Container>
      </GradientBanner>
      <Container className={cx('aboutContentContainer')}>
        {!!heroSection && (
          <React.Fragment>
            <FlexGrid>
              <FlexGrid.Column tablet={6}>
                <Header as="h2" className={cx('aboutSubHeader')}>Fuel Better With GasBuddy</Header>
                <Text as="p">
                  <Markup source={heroSection.description} />
                </Text>
                <br />
                <br />
                <Button
                  as="a"
                  className={cx('getStartedButton')}
                  href={heroSection.cta_url}
                  primary
                  size="lg"
                  wide
                >
                  {heroSection.cta_label}
                </Button>
              </FlexGrid.Column>
              <FlexGrid.Column tablet={6} desktopOffset={2} desktop={4} className={cx('phoneContainer')}>
                <Image
                  alt={heroSection.image_alt}
                  src={imageToSrc(heroSection.image)}
                />
              </FlexGrid.Column>
            </FlexGrid>
            <br />
            <br />
            <br />
          </React.Fragment>
        )}
        {page?.press_coverage.length && (
          <FlexGrid className={cx('quotesContainer')}>
            {page.press_coverage.map(quote => (
              <FlexGrid.Column tablet={4} className={cx('quoteContent')} key={quote.content}>
                <Text as="p" className={cx('quoteText')}>
                  <Markup source={quote.content} />
                </Text>
                <div className={cx('redDivider')} />
                <br />
                <Image
                  alt={quote.alt}
                  className={cx('quoteLogo')}
                  src={imageToSrc(quote.image)}
                />
              </FlexGrid.Column>
            ))}
          </FlexGrid>
        )}
        {(!!executives?.length || !!directors?.length) && (
          <React.Fragment>
            <div id="team" className={cx('leadershipTeamContainer')}>
              <Header as="h2" className={cx('aboutSubHeader')}>Leadership Team</Header>
              <br />
              {!!executives?.length && (
                <Modal
                  content={modalContent}
                  forceIsShowing={!!activeMember}
                  onClose={onCloseModal}
                  size="sm"
                >
                  <FlexGrid>
                    {executives.map((member, idx) => {
                      const { fullName, bioImage, mainTitle, subTitle } = buildTeamMember(member);
                      return (
                        <FlexGrid.Column
                          className={cx('leadershipTeamMember')}
                          key={fullName}
                          onClick={modalOpenHandlers[idx]}
                          mobile={6}
                          tablet={4}
                          desktop={3}
                        >
                          <div className={cx('hoverContent')}>
                            <Image
                              alt={fullName}
                              className={cx('memberImage')}
                              src={imageToSrc(bioImage)}
                            />
                          </div>
                          <br />
                          <Text bold color="blue" size="lg">{fullName}</Text>
                          <br />
                          <Text as="p" bold className={cx('memberTitle')}>
                            {mainTitle}
                          </Text>
                          {!!subTitle && (
                            <Text as="p" bold className={cx('memberTitle')}>
                              {subTitle}
                            </Text>
                          )}
                        </FlexGrid.Column>
                      );
                    })}
                  </FlexGrid>
                </Modal>
              )}
            </div>
            {!!directors?.length && (
              <div className={cx('directorTeamContainer')}>
                <FlexGrid>
                  {directors.map((director) => {
                    const fullName = `${director.firstName} ${director.lastName}`;
                    return (
                      <FlexGrid.Column
                        className={cx('directorTeamMember')}
                        key={director.title}
                        mobile={6}
                        tablet={4}
                        desktop={3}
                      >
                        <div className={cx('hoverContent')}>
                          <a
                            href={director.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            name={`${fullName} LinkedIn Profile Link`}
                          >
                            <Image
                              alt={fullName}
                              className={cx('memberImage')}
                              src={imageToSrc(director.bioImage)}
                            />
                          </a>
                        </div>
                        <br />
                        <Text bold color="blue" size="lg">{fullName}</Text>
                        <br />
                        <Text as="p" bold className={cx('memberTitle')}>
                          {director.title}
                        </Text>
                      </FlexGrid.Column>
                    );
                  })}
                </FlexGrid>
              </div>
            )}
          </React.Fragment>
        )}
      </Container>
      <div className={cx('learnMoreContainer')}>
        <Container>
          <Header as="h2" className={cx('aboutSubHeader')}>Learn more</Header>
          <Text as="p">
            Visit our press page to get the most up-to-date news, product updates and what the media is saying.
          </Text>
          <br />
          <br />
          <Button
            as="a"
            href="/newsroom"
            primary
            size="lg"
            wide
          >
            In The News
          </Button>
        </Container>
      </div>
    </Page>
  );
}

AboutUsPage.propTypes = {
  page: AboutUsPagePropType,
};

AboutUsPage.defaultProps = {
  page: undefined,
};
